/**
 * UranIA PA — Pannello "Onboarding richiesto" per il workspace Mod5.
 *
 * Renderizzato dalle route ``/workspace/mod5`` e ``/workspace/mod5/$docId``
 * quando il funzionario non ha ancora completato il flow ``/onboarding/pa``.
 * E' la pre-condizione UX prima di aprire la redazione documentale.
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';

export interface OnboardingRequiredScreenProps {
  readonly phase: 'loading' | 'incomplete' | 'error';
  readonly errorMessage?: string;
}

export function OnboardingRequiredScreen(
  props: OnboardingRequiredScreenProps,
): JSX.Element {
  const { t } = useTranslation();
  const isLoading = props.phase === 'loading';
  const isError = props.phase === 'error';

  return (
    <section
      className="urania-mod5-onboarding-gate container py-4"
      data-testid="mod5-onboarding-required"
      aria-labelledby="mod5-onboarding-required-heading"
    >
      <div
        className="rounded p-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <span
          className="badge mb-2"
          style={{
            backgroundColor: 'rgba(255,255,255,0.18)',
            color: '#fff',
            fontSize: '0.7rem',
            letterSpacing: '0.05em',
          }}
        >
          {t('workspace.mod5.onboarding_required.badge')}
        </span>
        <h1 id="mod5-onboarding-required-heading" className="h3 mb-2">
          {t('workspace.mod5.onboarding_required.title')}
        </h1>
        <p className="mb-0 opacity-90">
          {t('workspace.mod5.onboarding_required.subtitle')}
        </p>
      </div>

      {isLoading && (
        <div
          className="alert alert-info"
          role="status"
          aria-live="polite"
          data-testid="mod5-onboarding-required-loading"
        >
          {t('workspace.mod5.onboarding_required.loading')}
        </div>
      )}

      {isError && (
        <div
          className="alert alert-warning"
          role="alert"
          data-testid="mod5-onboarding-required-error"
        >
          <strong>{t('workspace.mod5.onboarding_required.error_title')}</strong>
          <p className="mb-0 small">
            {props.errorMessage ?? t('workspace.mod5.onboarding_required.error_body')}
          </p>
        </div>
      )}

      {!isLoading && (
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <h2 className="h5 mb-2">
              {t('workspace.mod5.onboarding_required.cta_heading')}
            </h2>
            <p className="text-muted mb-3 small">
              {t('workspace.mod5.onboarding_required.cta_body')}
            </p>
            <Link
              to="/onboarding"
              className="btn btn-primary"
              data-testid="mod5-onboarding-required-cta"
            >
              {t('workspace.mod5.onboarding_required.cta_button')} →
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}
